// ─────────────────────────────────────────────────────────────────────────────
// The currency table, as this app reads it — the framework's
// (`@parsina/framework/ui/lib/currency`, docs/clinic-currency/CONTRACTS.md §1),
// plus one thing the framework rightly refuses to decide for an app: what a screen
// does with a code that is NOT in the table.
//
// The framework throws for such a code, everywhere. That is right for a library
// and wrong for a reception desk: the BFF passes an unknown code on unchanged
// (a server can learn a currency before this build does), and a throw in a
// formatter blanks the panel. Folding it to IRR is FORBIDDEN (CONTRACTS §0): it
// would print a tenth of an unknown amount under «تومان».
//
// So an unknown code is KEPT and read as exactly what is known about it — an
// integer number of its minor units and its code:
//
//   • display divisor 1, no decimals: a figure is its RAW minor units, «12345 XYZ»;
//   • its unit word is the code itself, never «تومان»;
//   • parsing what a human typed is refused (NaN) — there is no scale to parse
//     against — and every money field of such a clinic is disabled with a note that
//     the app must be updated (components/common/MoneyInput.jsx);
//   • `fromDisplay` is the identity, so a figure pre-filled from the wire goes back
//     unchanged rather than rescaled;
//   • the first sight of each such code is reported once with console.error.
//
// Every app module reads the table through here, never the framework directly, so
// no call site can reach the throw.
// ─────────────────────────────────────────────────────────────────────────────

import {
  DEFAULT_CURRENCY, CURRENCIES, CURRENCY_CODES, isClinicCurrency, normalizeCurrency,
  assertSameCurrency, sumByCurrency, MixedCurrencyError,
  currencyInfo as tableInfo,
  toDisplay as tableToDisplay,
  fromDisplay as tableFromDisplay,
  parseAmount as tableParseAmount,
  unitLabel as tableUnitLabel,
} from '@parsina/framework/ui/lib/currency';

export {
  DEFAULT_CURRENCY, CURRENCIES, CURRENCY_CODES, isClinicCurrency, normalizeCurrency,
  assertSameCurrency, sumByCurrency, MixedCurrencyError,
};

/** A code that is neither blank (IRR) nor in the table — a server ahead of this build. */
export const isUnknownCurrency = (code) => code != null && code !== '' && !isClinicCurrency(code);

const reported = new Set();

/** console.error, once per unknown code per session. */
export function reportUnknownCurrency(code) {
  const key = String(code);
  if (reported.has(key)) return;
  reported.add(key);
  console.error(
    `[currency] ${JSON.stringify(key)} is not a currency this version of the app knows: `
    + 'its amounts are shown as raw minor units with the code, and money entry is disabled, '
    + 'until the app is updated.',
  );
}

function unknownInfo(code) {
  reportUnknownCurrency(code);
  const label = { long: String(code), short: String(code) };
  return {
    code: String(code),
    exponent: 0,
    displayDivisor: 1,
    minDisplayDecimals: 0,
    maxDisplayDecimals: 0,
    unknown: true,
    labels: { fa: label, en: label, ar: label, fr: label, de: label },
  };
}

/** The table row; for an unknown code, the raw-minor-units row described above. */
export function currencyInfo(code) {
  return isUnknownCurrency(code) ? unknownInfo(code) : tableInfo(code);
}

/** Minor units → display units (IRR: rials → toman). Unknown: the raw minor units. */
export function toDisplay(amountMinor, code) {
  if (!isUnknownCurrency(code)) return tableToDisplay(amountMinor, code);
  reportUnknownCurrency(code);
  if (amountMinor == null || amountMinor === '' || !Number.isFinite(Number(amountMinor))) return null;
  return Number(amountMinor);
}

/** Display units → minor units. Unknown: the identity (whole units), never rescaled. */
export function fromDisplay(displayValue, code) {
  if (!isUnknownCurrency(code)) return tableFromDisplay(displayValue, code);
  reportUnknownCurrency(code);
  // i18n-exempt: a PARSER's grouping marks (Latin «,» and Persian «٬»), stripped, never rendered.
  const n = Number(String(displayValue ?? '').replace(/[,٬\s]/g, ''));
  return Number.isFinite(n) ? Math.round(n) : NaN;
}

/** What a human typed → minor units, NaN when not an amount. Unknown: always NaN. */
export function parseAmount(input, code) {
  if (!isUnknownCurrency(code)) return tableParseAmount(input, code);
  reportUnknownCurrency(code);
  return NaN;
}

/** The unit word. Unknown: the code itself — never «تومان». */
export function unitLabel(code, locale, style) {
  if (!isUnknownCurrency(code)) return tableUnitLabel(code, locale, style);
  reportUnknownCurrency(code);
  return String(code);
}
